import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import EventIcon from '@mui/icons-material/Event'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import UpdateIcon from '@mui/icons-material/Update'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { MotivoPerdaDialog, DestinoPerda } from '@/components/pipeline/MotivoPerdaDialog'
import { ClasseScoreChip } from '@/components/pipeline/ClasseScoreChip'
import { tempoRelativo } from '@/components/pipeline/tempoRelativo'
import { ehConflitoVersao, erroMotivoPerdaDe, mensagemErroDe } from '@/components/pipeline/erros'
import { comMovimentacaoAgora } from '@/components/pipeline/movimentacao'
import { DESTINOS_PERDA, MSG_MOVER } from '@/components/pipeline/transicaoReserva'
import { ClasseScore, DadosPerda, ErroMotivoPerda, MSG_CONFLITO_VERSAO } from '@/types/scoreComercial'
import { oportunidadeService } from '@/services/oportunidadeService'
import { clienteService } from '@/services/clienteService'
import { usuarioService } from '@/services/usuarioService'
import { pendenciaService } from '@/services/pendenciaService'
import { useAuth } from '@/hooks/useAuth'
import { Oportunidade } from '@/types/oportunidade'
import { Cliente } from '@/types/cliente'
import {
  ORDEM_STATUS,
  STATUS_LABEL,
  STATUS_TERMINAIS,
  StatusReserva,
  corDoStatus,
  podeTransicionar,
} from '@/types/statusReserva'
import { QuadroRolavel } from '@/components/pipeline/QuadroRolavel'

interface KanbanCard {
  id: string
  numero?: number
  empresa: string
  valor: number
  produto: string
  status: StatusReserva
  consultor: string
  prazoReserva?: string | null
  pendenciasAbertas: number
  /** "O que fazer a seguir" (Fase 6, item 18) — persistido na reserva. */
  proximaAcao?: string | null
  /** Classe do Score Comercial (PDF §2.3) — vem pronta do backend, derivada da etapa. */
  classe?: ClasseScore | null
  /** Data da última mudança de etapa (backend). */
  ultimaMovimentacaoEm?: string | null
  /** Versão lida — vai em `versaoEsperada` no PATCH (409 se mudou). */
  versao?: number | null
}

/** Pendências que ainda demandam ação (espelha a inbox). */
const PENDENCIA_EM_ABERTO = ['ABERTA', 'VISUALIZADA', 'RESPONDIDA', 'EM_VALIDACAO', 'REABERTA']

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const formatNumeroReserva = (numero?: number) =>
  numero != null ? `RES-${String(numero).padStart(4, '0')}` : '—'

/** Situação do prazo: mesma janela de 7 dias dos jobs de alerta do backend. */
const prazoInfo = (
  prazoISO?: string | null,
): { label: string; cor: 'default' | 'warning' | 'error'; titulo: string } => {
  if (!prazoISO) return { label: '—', cor: 'default', titulo: 'Sem prazo definido' }
  const data = new Date(`${prazoISO}T00:00:00`)
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const dias = Math.round((data.getTime() - hoje.getTime()) / 86_400_000)
  const label = data.toLocaleDateString('pt-BR')
  if (dias < 0) return { label, cor: 'error', titulo: `Vencida há ${Math.abs(dias)} dia(s)` }
  if (dias === 0) return { label, cor: 'error', titulo: 'Vence hoje' }
  if (dias <= 7) return { label, cor: 'warning', titulo: `Vence em ${dias} dia(s)` }
  return { label, cor: 'default', titulo: `Vence em ${dias} dia(s)` }
}

const KanbanPage: FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()

  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([])
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [nomePorConsultor, setNomePorConsultor] = useState<Map<string, string>>(new Map())
  const [pendenciasPorReserva, setPendenciasPorReserva] = useState<Map<string, number>>(new Map())
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [mostrarEncerradas, setMostrarEncerradas] = useState(false)

  // Card sendo arrastado + coluna sob o cursor (feedback visual).
  const [arrastando, setArrastando] = useState<KanbanCard | null>(null)
  const [colunaAlvo, setColunaAlvo] = useState<StatusReserva | null>(null)
  const [movendo, setMovendo] = useState(false)

  // Perda em andamento: card + destino (CANCELADA/NAO_CONVERTIDA) + erro de campo do backend.
  const [perda, setPerda] = useState<{ card: KanbanCard; destino: DestinoPerda } | null>(null)
  const [erroPerda, setErroPerda] = useState<ErroMotivoPerda | null>(null)
  const [registrandoPerda, setRegistrandoPerda] = useState(false)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      // Diretório e pendências são complementos: se falharem, o quadro ainda funciona.
      const [pageOp, pageCli, usuarios, pendencias] = await Promise.all([
        oportunidadeService.listar(0, 200),
        clienteService.listar(0, 200).catch(() => ({ content: [] }) as never),
        usuarioService.listar({ max: 200 }).catch(() => []),
        pendenciaService.minhas().catch(() => []),
      ])
      setOportunidades(pageOp.content ?? [])
      setClientes(pageCli.content ?? [])
      setNomePorConsultor(new Map((usuarios ?? []).map((u) => [u.id, u.nome])))

      // Uma única chamada resolve a contagem de todas as reservas — sem N+1.
      const contagem = new Map<string, number>()
      for (const p of pendencias ?? []) {
        if (!PENDENCIA_EM_ABERTO.includes(p.status)) continue
        contagem.set(p.oportunidadeId, (contagem.get(p.oportunidadeId) ?? 0) + 1)
      }
      setPendenciasPorReserva(contagem)
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Falha ao carregar o pipeline')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const clienteById = useMemo(() => {
    const m = new Map<string, Cliente>()
    clientes.forEach((c) => m.set(c.id, c))
    return m
  }, [clientes])

  const cards = useMemo<KanbanCard[]>(
    () =>
      oportunidades.map((o) => {
        const cliente = clienteById.get(o.clienteId as unknown as string)
        const consultorId = o.usuarioResponsavel ?? undefined
        return {
          id: o.id,
          numero: o.numero,
          empresa:
            o.razaoSocial ??
            cliente?.razaoSocial ??
            `Cliente ${String(o.clienteId).slice(0, 8)}…`,
          valor: Number(o.valorEstimado ?? 0),
          produto: o.tipoNecessidade ?? '—',
          // Reserva antiga sem status cai em RASCUNHO, o início da jornada.
          status: ((o as { status?: StatusReserva }).status ?? 'RASCUNHO') as StatusReserva,
          consultor: consultorId ? (nomePorConsultor.get(consultorId) ?? '—') : '—',
          prazoReserva: o.prazoReserva,
          pendenciasAbertas: pendenciasPorReserva.get(o.id) ?? 0,
          proximaAcao: o.proximaAcao,
          classe: o.scoreComercial?.classe ?? null,
          ultimaMovimentacaoEm: o.ultimaMovimentacaoEm,
          versao: o.versao,
        }
      }),
    [oportunidades, clienteById, nomePorConsultor, pendenciasPorReserva],
  )

  const porStatus = useMemo(() => {
    const m = new Map<StatusReserva, KanbanCard[]>()
    ORDEM_STATUS.forEach((s) => m.set(s, []))
    cards.forEach((c) => m.get(c.status)?.push(c))
    return m
  }, [cards])

  // Terminais só viram coluna quando têm card (ou quando o usuário pede) — senão
  // o funil termina com três colunas vazias.
  const colunas = useMemo(
    () =>
      ORDEM_STATUS.filter(
        (s) =>
          !STATUS_TERMINAIS.includes(s) ||
          mostrarEncerradas ||
          (porStatus.get(s)?.length ?? 0) > 0,
      ),
    [porStatus, mostrarEncerradas],
  )

  /**
   * 409 `CONFLITO_VERSAO` (PDF §2.4): alguém mexeu na reserva depois que o quadro a leu.
   * Recarrega (versão e status novos) e avisa — o operador decide se repete.
   */
  const tratarConflito = async () => {
    await carregar()
    enqueueSnackbar(MSG_CONFLITO_VERSAO, { variant: 'warning' })
  }

  const aplicar = (id: string, atualizada: Partial<Oportunidade>) =>
    setOportunidades((prev) => prev.map((o) => (o.id === id ? { ...o, ...atualizada } : o)))

  const mover = async (card: KanbanCard, novoStatus: StatusReserva) => {
    if (!usuario?.id || card.status === novoStatus) return
    const destinoPerda = DESTINOS_PERDA[novoStatus]
    if (destinoPerda) {
      // Perdido exige motivo padronizado (8.6): o card só se move depois do diálogo.
      setErroPerda(null)
      setPerda({ card, destino: destinoPerda })
      return
    }
    setMovendo(true)
    const anterior = card.status
    // Otimista: o card muda de coluna na hora e volta se o backend recusar.
    aplicar(card.id, { status: novoStatus })
    try {
      const atualizada = await oportunidadeService.transicionarStatus(card.id, {
        novoStatus,
        usuarioId: usuario.id,
        versaoEsperada: card.versao ?? null,
      })
      // A resposta traz a versão nova e o score derivado — o card fica coerente sem reload.
      // `ultimaMovimentacaoEm` não vem no PATCH: a movimentação é agora.
      if (atualizada?.id) aplicar(card.id, comMovimentacaoAgora(atualizada))
      enqueueSnackbar(
        `${formatNumeroReserva(card.numero)}: ${STATUS_LABEL[anterior]} → ${STATUS_LABEL[novoStatus]}`,
        { variant: 'success' },
      )
    } catch (e) {
      aplicar(card.id, { status: anterior })
      if (ehConflitoVersao(e)) {
        await tratarConflito()
      } else {
        enqueueSnackbar(mensagemErroDe(e, MSG_MOVER), { variant: 'error' })
      }
    } finally {
      setMovendo(false)
    }
  }

  /**
   * CANCELADA / NAO_CONVERTIDA com motivo do catálogo. A versão vai da lista ATUAL: depois
   * de um 409 o quadro recarrega e a próxima tentativa já leva a versão nova, com o motivo
   * escolhido preservado no diálogo.
   */
  const confirmarPerda = async (dados: DadosPerda) => {
    if (!perda || !usuario?.id) return
    const { card, destino } = perda
    setRegistrandoPerda(true)
    setErroPerda(null)
    const versaoAtual = oportunidades.find((o) => o.id === card.id)?.versao ?? card.versao ?? null
    try {
      const atualizada = await oportunidadeService.transicionarStatus(card.id, {
        novoStatus: destino,
        observacao: dados.observacao,
        motivoPerdaCodigo: dados.motivoPerdaCodigo,
        usuarioId: usuario.id,
        versaoEsperada: versaoAtual,
      })
      aplicar(card.id, { ...comMovimentacaoAgora(atualizada?.id ? atualizada : {}), status: destino })
      enqueueSnackbar(
        `${formatNumeroReserva(card.numero)}: ${STATUS_LABEL[card.status]} → ${STATUS_LABEL[destino]}`,
        { variant: 'success' },
      )
      setPerda(null)
    } catch (e) {
      const erroCampo = erroMotivoPerdaDe(e)
      if (erroCampo) {
        setErroPerda(erroCampo)
      } else if (ehConflitoVersao(e)) {
        await tratarConflito()
      } else {
        enqueueSnackbar(mensagemErroDe(e, MSG_MOVER), { variant: 'error' })
      }
    } finally {
      setRegistrandoPerda(false)
    }
  }

  return (
    <Box>
      <PageHeader
        title="Pipeline Comercial"
        subtitle="Jornada real da reserva (18 status). Arraste um card para avançar a etapa."
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar o pipeline. {erro}
        </Alert>
      )}

      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Switch
            size="small"
            checked={mostrarEncerradas}
            onChange={(e) => setMostrarEncerradas(e.target.checked)}
          />
        }
        label={<Typography variant="caption">Mostrar colunas de encerramento vazias</Typography>}
      />

      {loading && !erro ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <QuadroRolavel aria-label="Colunas do pipeline">
          {colunas.map((status) => {
            const lista = porStatus.get(status) ?? []
            const alvoValido = arrastando != null && podeTransicionar(arrastando.status, status)
            const alvoInvalido =
              arrastando != null && arrastando.status !== status && !alvoValido

            return (
              <Box
                key={status}
                data-testid={`coluna-${status}`}
                sx={{ minWidth: 230, flex: '0 0 auto' }}
                onDragOver={(e) => {
                  // preventDefault é o que autoriza o drop no HTML5 DnD.
                  if (!alvoValido) return
                  e.preventDefault()
                  setColunaAlvo(status)
                }}
                onDragLeave={() => setColunaAlvo((c) => (c === status ? null : c))}
                onDrop={(e) => {
                  e.preventDefault()
                  const card = arrastando
                  setColunaAlvo(null)
                  setArrastando(null)
                  if (card && podeTransicionar(card.status, status)) void mover(card, status)
                }}
              >
                <Box
                  sx={{
                    bgcolor: corDoStatus(status),
                    color: '#fff',
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px 10px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    opacity: alvoInvalido ? 0.45 : 1,
                  }}
                >
                  {STATUS_LABEL[status]}
                  <Chip
                    label={lista.length}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      color: '#fff',
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    bgcolor: colunaAlvo === status ? 'rgba(46, 204, 113, 0.12)' : '#F4F6F9',
                    outline: colunaAlvo === status ? '2px dashed #2ECC71' : 'none',
                    borderRadius: '0 0 10px 10px',
                    p: 1,
                    minHeight: 200,
                    transition: 'background-color 120ms',
                  }}
                >
                  {lista.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', py: 3 }}
                    >
                      —
                    </Typography>
                  ) : (
                    lista.map((card) => {
                      const prazo = prazoInfo(card.prazoReserva)
                      return (
                        <Paper
                          key={card.id}
                          draggable={!movendo}
                          onDragStart={() => setArrastando(card)}
                          onDragEnd={() => {
                            setArrastando(null)
                            setColunaAlvo(null)
                          }}
                          onClick={() => navigate('/reservas')}
                          sx={{
                            p: 1.25,
                            mb: 1,
                            borderRadius: 1.5,
                            cursor: 'grab',
                            opacity: arrastando?.id === card.id ? 0.5 : 1,
                            '&:hover': { boxShadow: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography fontSize="0.7rem" color="text.secondary" fontWeight={600}>
                              {formatNumeroReserva(card.numero)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {/* Classe do Score Comercial (PDF §2.3) — do backend, derivada da etapa. */}
                              <ClasseScoreChip classe={card.classe} />
                              {card.pendenciasAbertas > 0 && (
                                <Tooltip title={`${card.pendenciasAbertas} pendência(s) em aberto`}>
                                  <Chip
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    icon={<AssignmentLateIcon sx={{ fontSize: 12 }} />}
                                    label={card.pendenciasAbertas}
                                    sx={{ height: 18, fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          <Typography fontSize="0.8rem" fontWeight={600} noWrap>
                            {card.empresa}
                          </Typography>
                          <Typography fontSize="0.85rem" fontWeight={700} color="success.main">
                            {formatBRL(card.valor)}
                          </Typography>

                          <Chip
                            label={card.produto}
                            size="small"
                            sx={{
                              mt: 0.5,
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: 'rgba(255, 98, 12,0.08)',
                              color: '#ff620c',
                            }}
                          />

                          <Box
                            sx={{
                              mt: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'text.secondary',
                            }}
                          >
                            <PersonOutlineIcon sx={{ fontSize: 13 }} />
                            <Typography fontSize="0.68rem" noWrap>
                              {card.consultor}
                            </Typography>
                          </Box>

                          <Tooltip title={prazo.titulo}>
                            <Box
                              sx={{
                                mt: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color:
                                  prazo.cor === 'error'
                                    ? 'error.main'
                                    : prazo.cor === 'warning'
                                      ? 'warning.main'
                                      : 'text.secondary',
                              }}
                            >
                              <EventIcon sx={{ fontSize: 13 }} />
                              <Typography fontSize="0.68rem">{prazo.label}</Typography>
                            </Box>
                          </Tooltip>

                          {/* Última movimentação (PDF §2.3): data da última mudança de etapa, do backend. */}
                          {tempoRelativo(card.ultimaMovimentacaoEm) && (
                            <Tooltip
                              title={`Última movimentação: ${new Date(card.ultimaMovimentacaoEm!).toLocaleString('pt-BR')}`}
                            >
                              <Box
                                sx={{
                                  mt: 0.25,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: 'text.secondary',
                                }}
                              >
                                <UpdateIcon sx={{ fontSize: 13 }} />
                                <Typography fontSize="0.68rem" data-testid="ultima-movimentacao">
                                  {tempoRelativo(card.ultimaMovimentacaoEm)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                          {/* Próxima ação (Fase 6, item 18): o card responde "o que fazer a
                              seguir" sem abrir a reserva — é para isso que o campo existe. */}
                          {card.proximaAcao && (
                            <Tooltip title={card.proximaAcao}>
                              <Box
                                sx={{
                                  mt: 0.75,
                                  pt: 0.75,
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.5,
                                }}
                              >
                                <FlagOutlinedIcon sx={{ fontSize: 13, mt: '1px', color: '#ff620c' }} />
                                <Typography
                                  fontSize="0.68rem"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {card.proximaAcao}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Paper>
                      )
                    })
                  )}
                </Box>
              </Box>
            )
          })}
        </QuadroRolavel>
      )}

      {/* Perdido (CANCELADA/NAO_CONVERTIDA) exige motivo do catálogo também no back-office. */}
      <MotivoPerdaDialog
        open={!!perda}
        empresa={perda?.card.empresa}
        destino={perda?.destino}
        submitting={registrandoPerda}
        erro={erroPerda}
        onClose={() => {
          setPerda(null)
          setErroPerda(null)
        }}
        onConfirm={(dados) => void confirmarPerda(dados)}
      />
    </Box>
  )
}

export default KanbanPage
